import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../../components/select/index';
import { StringKey } from '../../../../lang/index';

type FilterOption = { label: string; value: string };

/**
 * A single-choice filter drawn as the design's white 36px button. The empty value is "all", which
 * is the absence of a filter and is always the first option.
 */
export const FilterSelect: FC<{
  allLabel: StringKey;
  onChange: (value: string) => void;
  options: FilterOption[];
  value: string;
}> = ({ allLabel, onChange, options, value }) => (
  <Select<string> onChange={onChange} value={value}>
    {({ open }: { open: boolean }) => (
      <div className="relative">
        <SelectButton className="flex h-9 items-center gap-2 rounded border border-gray-100 bg-white pl-3 pr-1.5 shadow-xs">
          <span className="text-sm font-[450] text-gray-700">
            {options.find((option) => option.value === value)?.label ?? (
              <AppFormattedMessage id={allLabel} />
            )}
          </span>
          <span className="flex size-5 shrink-0 items-center justify-center">
            <ChevronDownIcon
              className={twMerge('size-[13.333px] transition-transform', open && 'rotate-180')}
            />
          </span>
        </SelectButton>
        {/* The anchor writes its own max-height inline, all the room the viewport has, so a
            class cannot cap it; the anchor reads its cap from this variable instead. */}
        <SelectOptions
          anchor={{ gap: 4, to: 'bottom end' }}
          className="pointer-events-auto z-[60] mt-0 min-w-40 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg [--anchor-max-height:16rem]"
          portal
        >
          <SelectOption value="">
            <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <AppFormattedMessage id={allLabel} />
            </span>
          </SelectOption>
          {options.map((option) => (
            <SelectOption key={option.value} value={option.value}>
              <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                {option.label}
              </span>
            </SelectOption>
          ))}
        </SelectOptions>
      </div>
    )}
  </Select>
);
